import React, { useEffect, useState } from 'react'

//MUI
import { Box, Typography, Grid, Skeleton } from '@mui/material'

//img
import SunIcon from '../../../../assets/resources/weather/sunny.svg'
import PartlyCloudyIcon from '../../../../assets/resources/weather/partly_cloudy.svg'
import CloudyIcon from '../../../../assets/resources/weather/cloudy.svg'
import DrizzleIcon from '../../../../assets/resources/weather/drizzle.svg'
import RainIcon from '../../../../assets/resources/weather/showers.svg'
import SnowIcon from '../../../../assets/resources/weather/scattered_snow.svg'
import StormIcon from '../../../../assets/resources/weather/strong_tstorms.svg'
import MoonIcon from '../../../../assets/resources/weather/clear.svg'
import PartlyCloudyMoonIcon from '../../../../assets/resources/weather/partly_clear.svg'

const WeatherWidget = ({isDay}) => {
    const [weather, setWeather] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [city, setCity] = useState(null);

    const DEFAULT_LOCATION = {
        latitude: 20.6597,   // GDL
        longitude: -103.3496
    };

    useEffect(() => {
        const fetchWeather = async (lat, lon) => {
            try {
                setLoading(true);

                const apiUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weathercode,uv_index&daily=temperature_2m_min,temperature_2m_max&timezone=auto`;


                const res = await fetch(apiUrl);
                const data = await res.json();

                setWeather(data);
                setError(null);

            } catch (err) {
                console.error(err);
                setError("Error al cargar el clima");
            } finally {
                setLoading(false);
            }
        };

        const getLocationByIP = async () => {
            try {
                const res = await fetch("https://ipapi.co/json/");
                const data = await res.json();

                if (data.latitude && data.longitude) {

                    setCity({
                        name: data.city,
                        region: data.region,
                        country: data.country_name
                    });

                    fetchWeather(data.latitude, data.longitude);
                } else {
                    fetchWeather(
                        DEFAULT_LOCATION.latitude,
                        DEFAULT_LOCATION.longitude
                    );
                }

            } catch (error) {
                fetchWeather(
                    DEFAULT_LOCATION.latitude,
                    DEFAULT_LOCATION.longitude
                );
            }
        };

        getLocationByIP();
    }, []);

    const getWeatherIcon = (code) => {

        const iconStyle = { width: 60, height: 60 };

        if (code === 0 && isDay) return <img src={SunIcon} alt="sun" style={iconStyle} />;
        if (code === 0 && !isDay) return <img src={MoonIcon} alt="sun" style={iconStyle} />;
        if ([1, 2].includes(code) && isDay) return <img src={PartlyCloudyIcon} alt="pcloudy" style={iconStyle} />;
        if ([1, 2].includes(code) && !isDay) return <img src={PartlyCloudyMoonIcon} alt="pcloudy" style={iconStyle} />;
        if (code === 3) return <img src={CloudyIcon} alt="cloudy" style={iconStyle} />;
        if ([51, 53, 55].includes(code)) return <img src={DrizzleIcon} alt="drizzle" style={iconStyle} />;
        if ([61, 63, 65].includes(code)) return <img src={RainIcon} alt="rain" style={iconStyle} />;
        if ([71, 73, 75].includes(code)) return <img src={SnowIcon} alt="snow" style={iconStyle} />;
        if ([95, 96, 99].includes(code)) return <img src={StormIcon} alt="storm" style={iconStyle} />;

        return <img src={SunIcon} alt="sun" style={iconStyle} />;
    };

    const getWeatherDescription = (code) => {

        const weatherMap = {
            0: "Soleado",
            1: "Mayormente soleado",
            2: "Parcialmente nublado",
            3: "Nublado",

            45: "Niebla",
            48: "Niebla con escarcha",

            51: "Llovizna ligera",
            53: "Llovizna moderada",
            55: "Llovizna intensa",

            61: "Lluvia ligera",
            63: "Lluvia moderada",
            65: "Lluvia intensa",

            71: "Nieve ligera",
            73: "Nieve moderada",
            75: "Nieve intensa",

            95: "Tormenta eléctrica",
            96: "Tormenta con granizo",
            99: "Tormenta fuerte con granizo"
        };

        return weatherMap[code] || "Clima desconocido";
    };

    const getUVLevel = (uv) => {
        if (uv <= 2) return { label: "Bajo", color: "#4CAF50" };
        if (uv <= 5) return { label: "Moderado", color: "#FFC107" };
        if (uv <= 7) return { label: "Alto", color: "#FF9800" };
        if (uv <= 10) return { label: "Muy alto", color: "#F44336" };
        return { label: "Extremo", color: "#9C27B0" };
    };

    return (
        <>
            {loading && (
                <>
                    <Skeleton width='20%' variant='text' sx={{ fontSize: '14px' }} />
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: '10px', my: 3 }}>
                        <Box>
                            <Skeleton variant="circular" width={60} height={60} />
                        </Box>
                        <Box sx={{ width: '100%' }}>
                            <Skeleton width='20%' variant='text' sx={{ fontSize: '34px' }} />
                            <Skeleton width='30%' variant='text' sx={{ fontSize: '14px' }} />
                            <Skeleton width='18%' variant='text' sx={{ fontSize: '14px' }} />
                        </Box>
                    </Box>
                    <Grid container spacing={1.5}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid item xxl={6} key={i}>
                                <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: '8px' }} />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {error && (
                <Typography color="error">{error}</Typography>
            )}

            {!loading && weather && (
                (() => {
                    const { current, daily } = weather;
                    const icon = getWeatherIcon(current.weathercode);
                    const weatherText = getWeatherDescription(current.weathercode);
                    const uv = Math.round(current.uv_index);
                    const uvInfo = getUVLevel(uv);
                    const tempAct = Math.round(current.temperature_2m);
                    const tempMin = Math.round(daily.temperature_2m_min[0]);
                    const tempMax = Math.round(daily.temperature_2m_max[0]);

                    return (
                        <>
                            <Typography variant='body2' sx={{ fontWeight: '700' }}>Clima actual</Typography>

                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: '10px', my: 3 }}>
                                <Box>
                                    {icon}
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'sans-serif' }}>
                                        {tempAct}°C
                                    </Typography>
                                    {city && (
                                        <Typography variant="body2" sx={{ fontWeight: '600' }}>
                                            {city.name}, {city.region}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        {weatherText}
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={1.5}>
                                <Grid item xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                                    <Box sx={{ p: 1.5, backgroundColor: '#F0F3F8', borderRadius: '8px' }}>
                                        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                                            Humedad
                                        </Typography>
                                        <Typography variant='body2' sx={{ fontWeight: '600', fontFamily: 'sans-serif' }}>
                                            {current.relative_humidity_2m} %
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                                    <Box sx={{ p: 1.5, backgroundColor: '#F0F3F8', borderRadius: '8px' }}>
                                        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                                            Viento
                                        </Typography>
                                        <Typography variant='body2' sx={{ fontWeight: '600', fontFamily: 'sans-serif' }}>
                                            {Math.round(current.wind_speed_10m)} km/h
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                                    <Box sx={{ p: 1.5, backgroundColor: '#F0F3F8', borderRadius: '8px' }}>
                                        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                                            Min / Max
                                        </Typography>
                                        <Typography variant='body2' sx={{ fontWeight: '600', fontFamily: 'sans-serif' }}>
                                            {tempMin}° / {tempMax}°
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                                    <Box sx={{ p: 1.5, backgroundColor: '#F0F3F8', borderRadius: '8px' }}>
                                        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                                            Índice UV
                                        </Typography>
                                        <Typography variant='body2' sx={{ fontWeight: '600', fontFamily: 'sans-serif' }}>
                                            {uvInfo.label}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </>
                    );
                })()
            )}
        </>
    )
}

export default WeatherWidget